import {
  Outlet,
  createRootRouteWithContext,
  useCanGoBack,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button, Heading, IconButton } from "@panelgenie/shared-ui";
import type { TrpcClient } from "../../trpc/client.js";
import type { CrashReporter } from "../../seams/crashReporter/index.js";

export type RouterContext = {
  readonly trpc: TrpcClient;
  readonly queryClient: QueryClient;
  readonly crashReporter: CrashReporter;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RootErrorComponent,
});

function RootLayout() {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  return (
    <>
      <header
        aria-label="app-chrome"
        className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-elevated px-3 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))]"
      >
        {canGoBack ? (
          <IconButton
            variant="ghost"
            size="md"
            label="Go back"
            icon={<BackArrowIcon />}
            onClick={() => router.history.back()}
          />
        ) : (
          <span className="w-11" aria-hidden="true" />
        )}
        <div className="flex-1" />
      </header>
      <Outlet />
    </>
  );
}

function BackArrowIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const context = Route.useRouteContext();
  const [eventId, setEventId] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void context.crashReporter
      .reportCrash({
        thrown: error,
        severity: "error",
        context: { boundary: "router-root" },
      })
      .then((outcome) => {
        if (cancelled) return;
        if (outcome.kind === "ok") setEventId(outcome.eventId);
      });
    return () => {
      cancelled = true;
    };
  }, [context.crashReporter, error]);

  return (
    <div
      role="alert"
      aria-label="app-crash-fallback"
      className="mx-auto max-w-xl p-6"
    >
      <Heading level="h1" className="mb-2">
        Something went wrong.
      </Heading>
      <p className="mb-4 text-mute">
        Panel Genie hit an unexpected error.
      </p>
      {eventId !== undefined && (
        <p
          aria-label="app-crash-event-id"
          className="mb-4 font-mono text-sm text-dim"
        >
          Reference id: {eventId}
        </p>
      )}
      <Button onClick={reset} aria-label="app-crash-recover">
        Try again
      </Button>
    </div>
  );
}

export { RootErrorComponent };
